import { Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { Table } from 'primeng/table';
import * as ExcelJS from 'exceljs';
import { Location } from '@angular/common';
import { PlanoDeContasService } from '../../services/plano-de-contas.service';
import { SpinnerService } from '../../services/spinner.service';
import { ToastService } from '../../services/toast.service';
import { AuthService } from '../../services/auth.service';
import { JwtHelper } from '../../helpers/jwtDecode.helper';
import { Dre } from '../../models/dre.model';
import { SeveridadeToast } from '../../models/enums/severidade-toast.enum';
import { Icones } from '../../shared/variaveisDeRotas';
import { retornaDescricao } from '../../shared/mock/descricao';

@Component({
  selector: 'app-dre',
  templateUrl: './dre.component.html',
  styleUrls: ['./dre.component.scss']
})
export class DreComponent implements OnInit {

  @Output() tituloChange = new EventEmitter<string>();
  @Output() iconeChange = new EventEmitter<string>();

  @Output() tituloDescricao = new EventEmitter<string>();
  @Output() textoDescricao = new EventEmitter<string>();
  @Output() mostrarDescricao = new EventEmitter<boolean>();

  @ViewChild('dt') dt: Table | undefined;

  private userId: string;
  dre: Dre = new Dre();
  periodo: Date | null = null;
  loading: boolean = false;

  objectKeys = Object.keys;

  dreTitles: { [key: string]: string } = {
    receitaOperacionalBruta: '(+) Receita Operacional Bruta',
    receitaPrestacaoServicosNacional: 'Receita de Serviços Nacionais',
    receitaPrestacaoServicosExterior: 'Receita de Serviços no Exterior',
    outrasReceitas: 'Outras Receitas',
    deducoesReceitaBruta: '(-) Deduções da Receita Bruta',
    vendasCanceladas: 'Vendas Canceladas',
    descontosIncondicionais: 'Descontos Incondicionais',
    demaisTributosPrestacaoServicos: 'Demais Tributos Incidentes Sobre Prestação de Serviços',
    receitaOperacionalLiquida: '(=) Receita Operacional Líquida',
    custoServicosPrestados: '(-) Custo dos Bens e Serviços',
    lucroBruto: '(=) Lucro Bruto',
    despesasOperacionais: '(-) Despesas Operacionais',
    proLaboreSocios: 'Pró-Labore dos Sócios',
    salariosRemuneracoesEmpregados: 'Salários e Remunerações dos Empregados',
    encargosSociaisPrevidencia: 'Encargos Sociais - Previdência Social',
    encargosSociaisFGTS: 'Encargos Sociais - FGTS',
    encargosSociaisOutros: 'Encargos Sociais - Outros',
    outrasDespesasOperacionais: 'Outras Despesas Operacionais',
    receitasFinanceiras: '(+) Receitas Financeiras',
    despesasFinanceiras: '(-) Despesas Financeiras',
    resultadoLiquidoAntesParticipacoes: '(=) Resultado Líquido Antes de Participações',
    participacoesEmpregados: 'Participações dos Empregados',
    participacoesSocios: 'Participações dos Sócios',
    resultadoLiquidoExercicio: '(=) Resultado Líquido do Exercício',
  };

  constructor(
    private planoDeContasService: PlanoDeContasService,
    private spinnerService: SpinnerService,
    private toastService: ToastService,
    private location: Location,
    private authService: AuthService,
    private jwtHelper: JwtHelper
  ) {
    this.userId = this.jwtHelper.getUserIdFromToken();
  }

  ngOnInit(): void {
    this.tituloChange.emit('DRE');
    this.iconeChange.emit(Icones.listaTopicoAmarelo);
    this.tituloDescricao.emit(retornaDescricao('dre').titulo);
    this.textoDescricao.emit(retornaDescricao('dre').texto);
    this.mostrarDescricao.emit(true)

    const now = new Date();
    this.periodo = new Date(now.getFullYear(), now.getMonth(), 1);
    this.carregarDRE();
  }

  carregarDRE(dataInicio?: Date, dataFim?: Date): void {
    if (!this.periodo && !dataInicio && !dataFim) return;

    this.loading = true;
    this.spinnerService.show();
    const dataParaEnviar = dataInicio || this.periodo;
    
    this.planoDeContasService.listarRelatorioDRE(dataParaEnviar!, this.userId).subscribe({
      next: (data) => {
        this.dre = data;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.toastService.setMensagemToast('Erro ao carregar o relatório DRE.', SeveridadeToast.Error);
      },
      complete: () => {
        this.spinnerService.hide();
      }
    });
  }

  ngOnDestroy(): void {
    this.mostrarDescricao.emit(false);
  }

  isBold(property: string): boolean {
    const boldProperties = [
      'receitaOperacionalBruta',
      'deducoesReceitaBruta',
      'receitaOperacionalLiquida',
      'lucroBruto',
      'despesasOperacionais',
      'receitasFinanceiras',
      'despesasFinanceiras',
      'resultadoLiquidoAntesParticipacoes',
      'resultadoLiquidoExercicio',
      'custoServicosPrestados'
    ];
    return boldProperties.includes(property);
  }

  applyFilterGlobal(value: string): void {
    if (this.dt) {
      this.dt.filterGlobal(value, 'contains');
    }
  }

  onDateChange(): void {
    if (this.periodo) {
      this.carregarDRE();
    }
  }

  voltar(): void {
    this.location.back();
  }

  downloadExcel() {
    this.spinnerService.show('Gerando relatório Excel...');
    
    let dadosParaExportar = Object.keys(this.dre);
    
    if (this.dt && this.dt.filteredValue) {
      dadosParaExportar = this.dt.filteredValue;
    }

    this.authService.obterUsuarioPorId(this.userId).subscribe({
      next: (user) => {
        const cpfCnpj = user.cpfCnpj || 'CNPJ não encontrado';

        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('DRE');

        const tituloRow = worksheet.addRow(['Demonstração do Resultado do Exercício']);
        tituloRow.font = { bold: true, underline: true };
        worksheet.mergeCells(`A${tituloRow.number}:D${tituloRow.number}`);
        tituloRow.alignment = { vertical: 'middle', horizontal: 'center' };

        const periodoTexto = this.periodo
          ? `De 01/01/${this.periodo.getFullYear()} até ${new Date(this.periodo.getFullYear(), this.periodo.getMonth() + 1, 0).toLocaleDateString('pt-BR')}`
          : 'Nenhum Período Selecionado';

        const cpfCnpjRow = worksheet.addRow(['CNPJ:', cpfCnpj]);
        const periodoRow = worksheet.addRow(['Período:', periodoTexto]);
        cpfCnpjRow.getCell(1).font = { bold: true };
        periodoRow.getCell(1).font = { bold: true };

        if (this.dt && this.dt.filteredValue && this.dt.filteredValue.length !== Object.keys(this.dre).length) {
          const filtroRow = worksheet.addRow(['Filtro de Pesquisa:', 'Aplicado']);
          filtroRow.getCell(1).font = { bold: true };
          const registrosRow = worksheet.addRow(['Registros Exportados:', `${dadosParaExportar.length} de ${Object.keys(this.dre).length}`]);
          registrosRow.getCell(1).font = { bold: true };
        }

        worksheet.addRow([]);

        const titleRow = worksheet.addRow([
          'Descrição',
          'Valor',
        ]);
        titleRow.font = { bold: true };
        titleRow.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFD3D3D3' },
        };

        worksheet.columns = [
          { key: 'descricao', width: 50 },
          { key: 'valor', width: 20, style: { numFmt: 'R$ #,##0.00' } },
        ];

        const noGrayBackground = [
          'custoServicosPrestados',
          'receitasFinanceiras',
          'despesasFinanceiras'
        ];

        dadosParaExportar.forEach((key) => {
          const value = Math.abs(this.dre[key]);
          const row = worksheet.addRow([
            this.getDreTitle(key),
            value,
          ]);

          if (this.isBold(key)) {
            row.getCell(1).font = { bold: true };
            row.getCell(2).font = { bold: true };

            if (!noGrayBackground.includes(key)) {
              row.getCell(1).fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFD3D3D3' },
              };
              row.getCell(2).fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFD3D3D3' },
              };
            }
          }
        });

        workbook.xlsx.writeBuffer().then((buffer) => {
          const blob = new Blob([buffer], { type: 'application/octet-stream' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          const dataHora = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
          a.download = `Relatório-DRE-${dataHora}.xlsx`;
          
          a.click();
          window.URL.revokeObjectURL(url);

          this.spinnerService.hide();
          this.toastService.setMensagemToast('Relatório DRE gerado com sucesso!', SeveridadeToast.Success);
        }).catch((error) => {
          console.error('Erro ao gerar Excel:', error);
          this.spinnerService.hide();
          this.toastService.setMensagemToast('Erro ao gerar relatório DRE.', SeveridadeToast.Error);
        });
      },
      error: (err) => {
        this.spinnerService.hide();
        this.toastService.setMensagemToast('Erro Interno.', SeveridadeToast.Error);
      }
    });
  }

  getDreTitle(property: string): string {
    return this.dreTitles[property] || property;
  }
}
